import { getStoredCompanyKey } from '@/services/api/erpat-client';

/**
 * Local "last edited" tracking for notes.
 *
 * ERPat's notes API has no `updated_at` field anywhere — not on the list, the
 * detail, or the create/update responses (checked against `docs/openapi.json`).
 * So there is no server signal that a note was edited, only when it was
 * CREATED. Without this, saving an edit to an old note left it exactly where
 * it was in "Recent" and every date-sorted list — as if nothing had happened.
 *
 * This fills the gap locally: a successful save through `notesService.update`
 * stamps the note id with the current time, kept in `localStorage` (survives a
 * reload, unlike an in-memory ref). Two honest limits, both inherent to being
 * client-only:
 *
 *  - It only sees edits made FROM SYNTRY. An edit made in ERPat's web leaves no
 *    trace here, so that note won't re-sort or show the "Edited" tag even
 *    though it changed.
 *  - It is PER DEVICE. Editing the same note from a second device doesn't
 *    update this one's record of it.
 *
 * Scoped by company key, matching the avatar directory and label-id discovery,
 * so switching tenants on one device can't leak one tenant's edit history into
 * another's note list.
 */

const STORAGE_PREFIX = 'syntry_note_edits:';

function storageKey(): string {
  const tenant = (getStoredCompanyKey() ?? '').trim().toLowerCase();
  return `${STORAGE_PREFIX}${tenant}`;
}

function readMap(): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKey());
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [id, at] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof at === 'number' && Number.isFinite(at)) out[id] = at;
    }
    return out;
  } catch {
    return {};
  }
}

function writeMap(map: Record<string, number>): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(map));
  } catch {
    /* private mode / quota — the edit still saved to ERPat, it just won't re-sort locally */
  }
}

/** Stamp a note as edited just now. Call after `notesService.update` resolves. */
export function recordNoteEdit(noteId: string, whenMs: number = Date.now()): void {
  if (!noteId) return;
  const map = readMap();
  map[noteId] = whenMs;
  writeMap(map);
}

/** The locally-recorded edit time for a note, or 0 when none is known. */
export function lastLocalEditAt(noteId: string): number {
  return readMap()[noteId] ?? 0;
}

/** Drop all locally-recorded edits for the signed-in tenant (sign-out). */
export function clearNoteEdits(): void {
  try {
    localStorage.removeItem(storageKey());
  } catch {
    /* nothing to clear */
  }
}
